/**
 * InvestigationsHistoryPage — lists every investigation the tenant has
 * run. Rows show source, question, status, verdict summary, and a
 * Replay button that reopens the exact investigation via the shared
 * InvestigatePanel (stream + verdict re-hydrate from persisted
 * `investigation_messages`).
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Sparkles, Loader2, Search, Play, Clock, Zap, XCircle, CheckCircle2, AlertTriangle, GitCompareArrows, CalendarClock, Trash2, Power, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { getTerm, useTerms, getBrandName } from '@/lib/branding';
import { InvestigatePanel } from '@/components/investigate/InvestigatePanel';

const STATUS_META = {
  completed: { icon: CheckCircle2, cls: 'text-emerald-400', label: 'Completed' },
  running: { icon: Loader2, cls: 'text-indigo-400 animate-spin', label: 'Running' },
  planning: { icon: Loader2, cls: 'text-indigo-400 animate-spin', label: 'Planning' },
  synthesising: { icon: Loader2, cls: 'text-indigo-400 animate-spin', label: 'Synthesising' },
  queued: { icon: Clock, cls: 'text-muted-foreground', label: 'Queued' },
  failed: { icon: AlertTriangle, cls: 'text-destructive', label: 'Failed' },
  cancelled: { icon: XCircle, cls: 'text-muted-foreground', label: 'Cancelled' },
};

const SOURCE_META = {
  kpi_monitor: 'KPI Monitor',
  dashboard: 'Dashboard',
  insight: 'AI Insight',
  palette: 'Command palette',
  discussion: 'Discussion',
};

function relativeTime(iso) {
  if (!iso) return '';
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function InvestigationsHistoryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useTerms();
  const openId = searchParams.get('open') || '';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [replayTarget, setReplayTarget] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState({ name: '', question: '', cadence: 'weekly' });

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[1], id]; // rolling 2-slot
      return [...prev, id];
    });
  };

  const refreshSchedules = async () => {
    try {
      const r = await api.listInvestigationSchedules();
      setSchedules(r.schedules || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { refreshSchedules(); }, []);

  const submitSchedule = async () => {
    if (!scheduleDraft.name.trim() || !scheduleDraft.question.trim()) {
      toast.error('Name and question required');
      return;
    }
    try {
      await api.createInvestigationSchedule({
        name: scheduleDraft.name.trim(),
        question: scheduleDraft.question.trim(),
        cadence: scheduleDraft.cadence,
        target: {},
      });
      setScheduleDraft({ name: '', question: '', cadence: 'weekly' });
      setShowScheduleForm(false);
      toast.success('Schedule created');
      refreshSchedules();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  const toggleSchedule = async (sch) => {
    try {
      await api.updateInvestigationSchedule(sch.schedule_id, { enabled: !sch.enabled });
      refreshSchedules();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  const removeSchedule = async (sch) => {
    try {
      await api.deleteInvestigationSchedule(sch.schedule_id);
      toast.success('Schedule removed');
      refreshSchedules();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  useEffect(() => {
    api.listInvestigations(100)
      .then((r) => setRows(r?.investigations || []))
      .catch((e) => toast.error(e.message || 'Failed to load investigations'))
      .finally(() => setLoading(false));
  }, []);

  // Auto-open a specific investigation when arriving from ?open=<id>
  // (e.g. clicking Reopen Investigation from a discussion thread). Only open
  // ONCE per id — closing the panel refetches `rows`, and without this guard
  // the effect would re-fire and reopen the panel (close→reopen loop).
  const autoOpenedRef = useRef('');
  useEffect(() => {
    if (!openId || !rows.length) return;
    if (autoOpenedRef.current === openId) return;
    const inv = rows.find((r) => r.investigation_id === openId);
    if (inv) {
      autoOpenedRef.current = openId;
      openReplay(inv);
    }
  }, [openId, rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.question?.toLowerCase().includes(q) ||
      r.verdict?.verdict?.toLowerCase().includes(q) ||
      r.source?.toLowerCase().includes(q)
    );
  }, [rows, query]);

  const openReplay = (inv) => {
    setReplayTarget({
      ...inv.target,
      metric: inv.target?.metric,
      model_id: inv.target?.model_id,
      window: inv.target?.window,
      source: inv.source,
      replay_investigation_id: inv.investigation_id,
      replay_question: inv.question,
    });
    setPanelOpen(true);
  };

  if (loading) {
    return (
      <div className="p-6" data-testid="investigations-loading">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="investigations-history-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-indigo-400" />
          {getTerm('investigations')}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every root-cause investigation your team has run — replay any of them to see the exact evidence trail again.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search by question, verdict, or source…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-9 rounded-lg"
            data-testid="investigations-search"
          />
        </div>
        <Button
          size="sm"
          className="h-9 text-xs gap-1 bg-primary text-primary-foreground"
          onClick={() => { setReplayTarget({ source: 'manual' }); setPanelOpen(true); }}
          data-testid="new-investigation-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New {getTerm('investigations', 'singular')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-9 text-xs gap-1"
          disabled={compareIds.length !== 2}
          onClick={() => navigate(`/investigations/diff?a=${compareIds[0]}&b=${compareIds[1]}`)}
          data-testid="investigations-compare-btn"
        >
          <GitCompareArrows className="w-3 h-3" /> Compare selected ({compareIds.length}/2)
        </Button>
      </div>

      {/* Scheduled auto-investigations */}
      <div className="rounded-xl border border-border/40 bg-card/20 p-4" data-testid="schedules-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <CalendarClock className="w-3.5 h-3.5 text-indigo-400" /> Scheduled auto-investigations
            <span className="text-[10px] text-muted-foreground font-normal">({schedules.length})</span>
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowScheduleForm((v) => !v)} className="h-7 text-[11px] gap-1" data-testid="schedules-toggle-form">
            <Plus className="w-3 h-3" /> {showScheduleForm ? 'Cancel' : 'New schedule'}
          </Button>
        </div>
        {showScheduleForm && (
          <div className="mb-3 grid gap-2 md:grid-cols-[1fr,2fr,140px,80px]" data-testid="schedules-form">
            <Input placeholder="Name" value={scheduleDraft.name} onChange={(e) => setScheduleDraft((s) => ({ ...s, name: e.target.value }))} className="h-8 text-xs" data-testid="schedule-name-input" />
            <Input placeholder="Question — e.g. Why did GWP change this period?" value={scheduleDraft.question} onChange={(e) => setScheduleDraft((s) => ({ ...s, question: e.target.value }))} className="h-8 text-xs" data-testid="schedule-question-input" />
            <select
              value={scheduleDraft.cadence}
              onChange={(e) => setScheduleDraft((s) => ({ ...s, cadence: e.target.value }))}
              className="h-8 text-xs rounded-md border border-border/50 bg-muted/20 px-2"
              data-testid="schedule-cadence-select"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <Button size="sm" onClick={submitSchedule} className="h-8 text-[11px]" data-testid="schedule-save">Save</Button>
          </div>
        )}
        {schedules.length === 0 ? (
          <p className="text-[11px] text-muted-foreground" data-testid="schedules-empty">No schedules yet. Click New schedule to have {getBrandName()} rerun a question weekly/monthly.</p>
        ) : (
          <div className="space-y-1.5">
            {schedules.map((sch) => (
              <div key={sch.schedule_id} className="flex items-center gap-2 text-xs p-2 rounded-md border border-border/30 bg-card/30" data-testid={`schedule-row-${sch.schedule_id}`}>
                <button type="button" onClick={() => toggleSchedule(sch)} className={sch.enabled ? 'text-emerald-400' : 'text-muted-foreground'} title={sch.enabled ? 'Enabled — click to pause' : 'Paused — click to enable'} data-testid={`schedule-toggle-${sch.schedule_id}`}>
                  <Power className="w-3 h-3" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground/95 truncate">{sch.name}</span>
                    <Badge className="text-[9px] bg-muted/30 border border-border/50 font-mono">{sch.cadence}</Badge>
                    <span className="text-[10px] text-muted-foreground">runs: {sch.runs_count}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground truncate">{sch.question}</p>
                </div>
                {sch.last_investigation_id && (
                  <button type="button" onClick={() => navigate(`/investigations?open=${sch.last_investigation_id}`)} className="text-[10px] text-indigo-300 hover:text-indigo-200" data-testid={`schedule-open-latest-${sch.schedule_id}`}>
                    Open latest
                  </button>
                )}
                <button type="button" onClick={() => removeSchedule(sch)} className="text-muted-foreground hover:text-destructive" title="Delete schedule" data-testid={`schedule-delete-${sch.schedule_id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-border/40 bg-card/30 p-10 text-center text-sm text-muted-foreground" data-testid="investigations-empty">
          No {getTerm('investigations', 'plural', 'lower')} yet. Trigger one from a KPI card, dashboard widget, or {getTerm('insights', 'singular', 'lower')}.
        </div>
      ) : (
        <div className="space-y-2" data-testid="investigations-list">
          {filtered.map((inv) => {
            const meta = STATUS_META[inv.status] || STATUS_META.queued;
            const Icon = meta.icon;
            const source = SOURCE_META[inv.source] || inv.source;
            return (
              <div
                key={inv.investigation_id}
                className={`rounded-xl border ${compareIds.includes(inv.investigation_id) ? 'border-indigo-500/60 bg-indigo-500/5' : 'border-border/50 bg-card/40'} p-4 flex items-start gap-4 hover:border-primary/30 transition-colors`}
                data-testid={`investigation-row-${inv.investigation_id}`}
              >
                <input
                  type="checkbox"
                  className="mt-1 w-3.5 h-3.5"
                  checked={compareIds.includes(inv.investigation_id)}
                  onChange={() => toggleCompare(inv.investigation_id)}
                  title="Select for compare"
                  data-testid={`investigation-compare-select-${inv.investigation_id}`}
                />
                <div className={`mt-0.5 ${meta.cls}`}><Icon className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs font-semibold text-foreground truncate">{inv.question}</span>
                    <Badge className="text-[9px] bg-muted text-muted-foreground">{source}</Badge>
                    {inv.target?.metric && <Badge className="text-[9px] bg-indigo-500/15 text-indigo-300 border-indigo-500/25 font-mono">{inv.target.metric}</Badge>}
                    <span className="text-[10px] text-muted-foreground ml-auto">{relativeTime(inv.created_at)}</span>
                  </div>
                  {inv.verdict?.verdict && (
                    <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2" data-testid={`investigation-verdict-${inv.investigation_id}`}>
                      {inv.verdict.verdict}
                    </p>
                  )}
                  {inv.status === 'failed' && inv.error && (
                    <p className="text-[11px] text-destructive/90 mt-1">{inv.error}</p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openReplay(inv)}
                  className="h-7 gap-1 rounded-lg text-[10px]"
                  data-testid={`investigation-replay-${inv.investigation_id}`}
                >
                  <Play className="w-3 h-3" /> Replay
                </Button>
                {inv.status === 'completed' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const fresh = await api.rerunInvestigation(inv.investigation_id);
                        toast.success('Rerun started — opening panel');
                        setReplayTarget({
                          replay_investigation_id: fresh.investigation_id,
                          metric: (inv.target || {}).metric,
                          model_id: (inv.target || {}).model_id,
                        });
                        setPanelOpen(true);
                        // Optimistically insert the new inv row at the top
                        setRows((prev) => [{ ...fresh, status: 'queued' }, ...prev]);
                      } catch (err) { toast.error(err.message || 'Rerun failed'); }
                    }}
                    className="h-7 gap-1 rounded-lg text-[10px]"
                    data-testid={`investigation-rerun-${inv.investigation_id}`}
                  >
                    <RefreshCw className="w-3 h-3" /> Rerun
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <InvestigatePanel
        open={panelOpen}
        onOpenChange={(v) => {
          setPanelOpen(v);
          if (!v) {
            // Refresh the list so a newly-started investigation appears.
            api.listInvestigations(100).then((r) => setRows(r?.investigations || [])).catch(() => {});
          }
        }}
        target={replayTarget}
      />
    </div>
  );
}
